import chunk from "lodash/chunk";
import API from "serviceshift-ui/api-client";
import { getProperty } from "@/lib/helpers.js";

import CONSTANTS from "@/Constants.js";
const { CALL_ROUTING_STATUS } = CONSTANTS;

const parseTranscript = (transcript) => {
  if (!transcript) return [];
  const transcriptMessages = transcript
    .split(new RegExp(`(Agent|Caller): `))
    .filter((message) => !!message);

  // Just in case this is not a conversation (ex: voicemail), return the message from the caller
  if (transcriptMessages.length === 1) {
    return [
      {
        user: "Caller",
        message: transcript
      }
    ];
  }
  return chunk(transcriptMessages, 2).map((row) => ({
    user: row[0],
    message: row[1]
  }));
};

// IMPORTANT: If you change this you must update visible_for_user in call.rb because This MUST logically be the
// inverse of the initial calls to include which are loaded in the visible_for_user scope.
const isCallExpired = (call, currentAdminId) =>
  completedUnclaimed(call) || claimedSessionEnded(call, currentAdminId);

const completedUnclaimed = (call) => {
  if (!call) return false;
  return (
    call.routing_status === CALL_ROUTING_STATUS.COMPLETED &&
    !call.claimed_by &&
    olderThanADay(call)
  );
};

const claimedSessionEnded = (call, currentAdminId) =>
  getProperty(call, "claimed_by.id") === currentAdminId &&
  (!call.session_started_at || call.session_ended_at);

const olderThanADay = (call) =>
  new Date().getTime() - call.startedAt?.getTime() > 24 * 60 * 60 * 1000; // 1 day in milliseconds

const removeCall = (calls, call) =>
  calls.filter((_call) => _call.id !== call.id);
const upsertCall = (_calls, call) => {
  const calls = [..._calls];
  const callIndex = calls.findIndex((_call) => _call.id === call.id);
  if (callIndex > -1) {
    calls[callIndex] = { ...call };
  } else {
    calls.push({ ...call });
  }
  return calls;
};

export default {
  assignCall(_, { link, admin_id }) {
    return API.makeRequest(link, {
      method: "PUT",
      data: { admin_id }
    });
  },
  claimCall(_, link) {
    return API.makeRequest(link, {
      method: "PUT"
    });
  },
  cleanUpCallSessions({ commit, state }) {
    const calls = state.tenantCalls.filter((call) =>
      isCallExpired(call, getProperty(state, "currentAdmin.id"))
    );
    commit("replace", {
      state: "tenantCalls",
      data: calls
    });
  },
  async fetchCall(_, callId) {
    const response = await API.makeRequest(`/v2/phone_calls/${callId}`, {
      method: "GET"
    });
    response.data.transcriptParsed = parseTranscript(response.data.transcript);
    return response;
  },
  linkCall(_, { link, call_id }) {
    return API.makeRequest(link, {
      method: "POST",
      data: { call_id }
    });
  },
  processCall({ commit, state }, call) {
    call.transcriptParsed = parseTranscript(call.transcript || "");
    const { tenantCalls, currentAdmin } = state;
    let calls = [...tenantCalls];
    // if session ended, remove from store
    if (isCallExpired(call, getProperty(currentAdmin, "id"))) {
      calls = removeCall(calls, call);
    } else {
      calls = upsertCall(calls, call);
    }
    commit("replace", {
      state: "tenantCalls",
      data: calls
    });
  },
  setCalls({ commit }, data) {
    commit("replace", {
      state: "tenantCalls",
      data: data.map((event) => event.call)
    });
  },
  unclaimCall(_, link) {
    return API.makeRequest(link, {
      method: "PUT"
    });
  },
  unlinkCall(_, { link, call_id }) {
    return API.makeRequest(link, {
      method: "DELETE",
      data: { call_id }
    });
  },
  endSession(_, link) {
    return API.makeRequest(link, {
      method: "PUT"
    });
  },
  updateCallNote(_, { addNotePath, comment, callId }) {
    return API.makeRequest(addNotePath, {
      method: "POST",
      data: {
        comment: {
          comment: comment,
          commentable_type: "Call",
          commentable_id: callId,
          message_key: "Note:Admin"
        }
      }
    });
  },
  removeCallNote(_, { removeNodePath }) {
    return API.makeRequest(removeNodePath, {
      method: "DELETE"
    });
  },
  async fetchCallDetails(_, callId) {
    const response = await API.makeRequest(
      `/v2/phone_calls/${callId}/details`,
      {
        method: "GET"
      }
    );
    return response.data;
  }
};
